import {useEffect, useRef} from 'react';

type LockId = symbol;

type BodyStyles = {
  overflowY: string;
  position: string;
  top: string;
};

const bodyState = {
  lockIds: new Set<LockId>(),
  scrollY: 0,
  prevStyles: {
    overflowY: '',
    position: '',
    top: '',
  },
};

function getBodyStyles(): BodyStyles {
  const {style} = document.body;

  return {
    overflowY: style.overflowY,
    position: style.position,
    top: style.top,
  };
}

function setBodyStyles(styles: BodyStyles): void {
  const {style} = document.body;
  style.overflowY = styles.overflowY;
  style.position = styles.position;
  style.top = styles.top;
}

function releaseBodyScrollLock(lockId: LockId): void {
  if (!bodyState.lockIds.delete(lockId) || bodyState.lockIds.size > 0) {
    return;
  }
  setBodyStyles(bodyState.prevStyles);
  window.scrollTo(0, bodyState.scrollY);
}

function acquireBodyScrollLock(lockId: LockId): void {
  if (bodyState.lockIds.has(lockId)) {
    return;
  }

  if (bodyState.lockIds.size === 0) {
    bodyState.prevStyles = getBodyStyles();
    bodyState.scrollY = window.scrollY;
    setBodyStyles({
      overflowY: 'hidden',
      position: 'fixed',
      top: `-${bodyState.scrollY}px`,
    });
  }

  bodyState.lockIds.add(lockId);
}

export function useBodyScrollLock(isEnabled: boolean = true): void {
  const lockIdRef = useRef<LockId | null>(null);

  if (lockIdRef.current === null) {
    lockIdRef.current = Symbol('body-scroll-lock');
  }

  const lockId = lockIdRef.current;

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    acquireBodyScrollLock(lockId);

    return () => {
      releaseBodyScrollLock(lockId);
    };
  }, [isEnabled, lockId]);
}
